import cn from 'classnames';
import { FC } from 'react';
import { useIntl } from 'react-intl';

import { patientAgentMessages } from '../config';

import styles from './AgentChat.module.scss';

type EventItem = {
  id: string;
  tone: 'running' | 'done' | 'error';
  label: string;
  completedAt?: number;
  detail?: string;
  startedAt?: number;
  visual: 'progress' | 'check' | 'image' | 'error';
};

type EventEntryProps = {
  items: EventItem[];
  isFirstWorkEvent: boolean;
  workContextLine?: string;
  className?: string;
};

export const EventEntry: FC<EventEntryProps> = ({
  items,
  isFirstWorkEvent,
  workContextLine,
  className,
}) => {
  const { formatMessage, formatNumber } = useIntl();
  const isWorking = items.some((item) => item.tone === 'running');
  const startedAt = Math.min(
    ...items.flatMap((item) =>
      item.startedAt === undefined ? [] : [item.startedAt],
    ),
  );
  const completedAt = Math.max(
    ...items.flatMap((item) =>
      item.completedAt === undefined ? [] : [item.completedAt],
    ),
  );
  const durationSeconds =
    Number.isFinite(startedAt) && Number.isFinite(completedAt)
      ? Math.max(1, Math.round((completedAt - startedAt) / 1000))
      : undefined;
  const duration = durationSeconds
    ? formatNumber(
        durationSeconds < 60
          ? durationSeconds
          : durationSeconds < 3600
            ? Math.max(1, Math.round(durationSeconds / 60))
            : Math.max(1, Math.round(durationSeconds / 3600)),
        {
          style: 'unit',
          unit:
            durationSeconds < 60
              ? 'second'
              : durationSeconds < 3600
                ? 'minute'
                : 'hour',
          unitDisplay: 'long',
        },
      )
    : undefined;
  const label = isWorking
    ? formatMessage(patientAgentMessages.agentIsWorking)
    : duration
      ? formatMessage(patientAgentMessages.agentWorkedFor, { duration })
      : formatMessage(patientAgentMessages.agentWorked);

  return (
    <div className={cn(styles.threadEventEntry, className)}>
      {isFirstWorkEvent && workContextLine ? (
        <p className={styles.workContext}>{workContextLine}</p>
      ) : null}
      <div className={styles.threadEvent} role="status" aria-live="polite">
        <p className={styles.threadEventLabel}>{label}</p>
      </div>
    </div>
  );
};
